import { useState, useEffect } from "react";
import { FaMapMarkedAlt, FaCity } from "react-icons/fa";
import CookiesServices from "../../services/CookiesService";
import sriLanka from "../../assets/sri_lanka.png";

const useTop10 = () => {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]); 
  const [timeframe, setTimeframe] = useState("mtd");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  const [designationLevels, setDesignationLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");

  const cookiesServices = new CookiesServices();
  const [accessToken, setAccessToken] = useState(cookiesServices.get("AccessToken"));
  const [UserName, setUserName] = useState(cookiesServices.get("UserName"));
  const [kpiMode, setKpiMode] = useState(cookiesServices.get("kpiMode"));
  const [kpiUser, setKpiUser] = useState(cookiesServices.get("kpiUser"));
  const [hasProfile, setHasProfile] = useState(cookiesServices.get("hasProfile"));
  const [ImpersonatedUser, setImpersonatedUser] = useState(cookiesServices.get("ImpersonatedUser"));
  const headers = cookiesServices.getHeaders();

  // Base visual properties for known categories
  const baseVisuals = {
    national: { icon: <img src={sriLanka} alt="Sri Lanka" className="icon-image" />, color: "#007bff" },
    zone: { icon: <FaMapMarkedAlt />, color: "#28a745" },
    region: { icon: <FaCity />, color: "#ff9800" },
    others: { icon: <FaCity />, color: "#9c27b0" } 
  };

  // Handle cookie-level updates
  useEffect(() => {
    const handleLevelChange = () => {
      setAccessToken(cookiesServices.get("AccessToken"));
      setUserName(cookiesServices.get("UserName"));
      setKpiMode(cookiesServices.get("kpiMode"));
      setKpiUser(cookiesServices.get("kpiUser"));
      setHasProfile(cookiesServices.get("hasProfile"));
      setImpersonatedUser(cookiesServices.get("ImpersonatedUser"));
    };
    window.addEventListener("level_changed", handleLevelChange);
    return () => window.removeEventListener("level_changed", handleLevelChange);
  }, [cookiesServices]);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleError = (status) => {
    if (status === 401) {
      setFeedback({
        message: "Your session has expired. Please sign in again to continue..",
        type: "expire",
      });
      cookiesServices.clearLogin();
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}`;
    } else if (status === 500) {
      setFeedback({
        message: "Internal Server Error. Please try again later.",
        type: "error",
      });
      cookiesServices.clearLogin();
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}`
    } else {
      setFeedback({
        message: "Failed to fetch data. Please try again later.",
        type: "error",
      });
    }
  };

  // Fetch leaderboard data
  const fetchLeaderboard = async (mode = timeframe, level = selectedLevel) => {
  if (!accessToken || !UserName) { 
    setError(error.message || "Missing authentication details.");
    setFeedback({ message: "Missing authentication details.", type: "error" });
    return;
  }

  setLoading(true);
  setFeedback(null);

    try {
      const res = await fetch(cookiesServices.toUrl('v1', `/leaderboard?level=${level}&mode=${mode.toUpperCase()}`), {
        method: 'GET',
        headers,
      });
      if (!res.ok) {
        handleError(res.status);
        return;
      }

      const json = await res.json();
      if (json.status === "Success" && json.data) {
        setData(json.data);

        // Dynamic categories with visuals
        const catList = Object.keys(json.data).map((key) => ({
          key,
          title: json.data[key]?.title || key.toUpperCase(),
          icon: baseVisuals[key]?.icon || <FaCity />,
          color: baseVisuals[key]?.color || "#6c757d",
        }));
        setCategories(catList);

        // Set first category selected initially
        if (!selectedCategory) setSelectedCategory(catList[0]?.key || null);
      } else {
        setFeedback({ message: json.message || "Failed to fetch leaderboard.", type: "error" });
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
      setError(error.message || "Error fetching leaderboard.");
      setFeedback({ message: "Error fetching leaderboard.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  const fetchDesignationLevels = async () => {
    try {
      const res = await fetch(cookiesServices.toUrl('v1', `/leaderboard/designation-levels`), {
        method: "GET",
        headers,
      });

      if (!res.ok) {
        handleError(res.status);
        return;
      }

        const json = await res.json();
        if (json.status === "Success" && Array.isArray(json.data)) {
          setDesignationLevels(json.data);
          setSelectedLevel(""); // blank first selection
        } else {
          setFeedback({ message: json.message || "Failed to fetch designation levels.", type: "error" });
        }
      } catch (err) {
        console.error("Error fetching designation levels:", err);
        setError("Error fetching designation levels");
        setFeedback({ message: "Error fetching designation levels", type: "error" });
      }
    };

    fetchDesignationLevels();
  }, [accessToken, UserName, kpiMode, kpiUser]);

  // Fetch data when level, timeframe changes
  useEffect(() => {
    fetchLeaderboard(timeframe, selectedLevel);
  }, [accessToken, UserName, kpiMode, kpiUser, timeframe, selectedLevel]);

  const getPlayers = (category) => data?.[category]?.topRanks || [];
  const getRank = (category) => data?.[category]?.rank || 0;

  return {
    data,
    timeframe,
    setTimeframe,
    selectedCategory,
    setSelectedCategory,
    isMobile,
    loading,
    feedback,
    error,
    fetchLeaderboard,
    getPlayers,
    getRank,
    categories,
    designationLevels,
    selectedLevel,
    setSelectedLevel,
    hasProfile,
    setFeedback,
  };
};

export default useTop10;
